import {find, isNumber, some} from 'lodash';
import {useEffect, useState, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ActivityStatistic} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {getPercentage} from 'utils/transform';

import {ActivityItem} from '../ActivityItem';
import {ActivityMetric} from '../enums';
import {ACTIVITY_METRICS_CONFIGS, DURATION_ACTIVITY_METRICS_CONFIGS} from '../metric-config';
import {ActivityMetricConfig} from '../types';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.DASHBOARD;

type Props = {
    viewType?: 'card' | 'row';
    metric: ActivityMetric;
    selected?: boolean;
    statistic: ActivityStatistic;
    onClick?: () => void;
}

export const ActivityMetricItem: React.FC<Props> = ({viewType = 'card', metric, selected, statistic, onClick}: Props) => {
    const {t} = useTranslation();
    const [metricConfig, setMetricConfig] = useState<ActivityMetricConfig>(null);

    const {title, tooltip, totalGetter, valueGetter, valueFormatter, color} = metricConfig || {};
    const isDurationMetric = useMemo(() => some(DURATION_ACTIVITY_METRICS_CONFIGS, (config) => config.metric === metric), [metric]);
    const value = useMemo(() => statistic ? valueGetter?.(statistic) : null, [statistic, valueGetter]);
    const formattedValue = useMemo(() => value && valueFormatter ? valueFormatter(value) : value, [value, valueFormatter]);
    const total = useMemo(() => statistic ? totalGetter?.(statistic) : null, [statistic, totalGetter]);

    useEffect(() => {
        setMetricConfig(find(ACTIVITY_METRICS_CONFIGS, {metric}));
    }, [metric]);

    return metricConfig ?
        (
            <ActivityItem
                viewType={viewType}
                color={color}
                title={title}
                selected={selected}
                tooltip={tooltip}
                content={
                    <div className='tw-text-base sm:tw-ts-h5'>
                        <span className='tw-font-bold'>{formattedValue}</span>
                        {isDurationMetric && <span>{` (${t(T_KEYS.LABELS.MINUTES)})`}</span>}
                        {!!total && isNumber(value) && <span>{` (${getPercentage(value, total)}%)`}</span>}
                    </div>
                }
                onClick={onClick}
            />
        )
        : null;
};
